// Coalesced async saver. Repeated calls collapse into a single
// in-flight request whose payload is whatever was queued last when
// the previous round-trip resolves. Used by view-templates'
// `saveCurrentView` to keep a flurry of `selectedPorts` writes
// (drag-reorder loop, multi-add, etc.) from racing against each other
// on the wire.
//
// Behaviour:
//   - First call kicks off the operation immediately with the given
//     payload.
//   - While that operation is in flight, additional calls update the
//     queued payload (last write wins) but don't issue new requests.
//   - When the in-flight op settles, if a payload is queued, fire a
//     fresh op with that payload — and repeat.
//   - On error the callback's rejection is reported via `onError`;
//     the queue is not retried automatically (next user-driven save
//     will pick up where it left off).

export interface CoalescedSaverOptions<T> {
  // The async operation. Receives the payload to save.
  save: (payload: T) => Promise<unknown>;
  // Called on each rejection. Defaults to a no-op so the queue can
  // keep running; pass a logger if you want visibility.
  onError?: (err: unknown) => void;
}

export interface CoalescedSaver<T> {
  // Queue a payload. Returns immediately; the actual save runs async.
  enqueue: (payload: T) => void;
  // Resolves once every queued payload has been flushed. Useful in
  // tests and in `pagehide`-style "drain before exit" hooks.
  drain: () => Promise<void>;
  // For introspection in tests.
  isPending: () => boolean;
}

export function createCoalescedSaver<T>(opts: CoalescedSaverOptions<T>): CoalescedSaver<T> {
  let pending: Promise<void> | null = null;
  let queued: { payload: T } | null = null;

  function run(): void {
    if (pending) return;
    const flush = async (): Promise<void> => {
      while (queued) {
        const next = queued;
        queued = null;
        try {
          await opts.save(next.payload);
        } catch (err) {
          opts.onError?.(err);
        }
      }
    };
    pending = flush().finally(() => { pending = null; });
  }

  return {
    enqueue(payload) {
      queued = { payload };
      run();
    },
    async drain() {
      // Wait for the currently-pending flush (which itself loops
      // until queued is null). If a new enqueue lands while we're
      // awaiting, the existing flush picks it up on its next loop.
      while (pending) await pending;
    },
    isPending: () => pending !== null,
  };
}
